import React, { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import ReactHtmlParser from 'react-html-parser';

import isKiosk from '../../../utils/isKiosk';
import openChatWidget from '../../../utils/openChatWidget';
import { ContentfulButtonLinkColor, ContentfulButtonLinkSize, ContentfulCtaTarget } from '../../../ContentfulTypes';

import {
  InternalButton,
  ExternalButton,
  FunctionalButton,
  Spinner,
} from './styles';

interface Props {
  buttonText: string;
  buttonColor?: ContentfulButtonLinkColor;
  buttonSize?: ContentfulButtonLinkSize;
  ariaLabel?: string;
  url?: string;
  route?: ContentfulCtaTarget;
  spinner?: boolean;
  className?: string;
  children?: ReactNode;
  bladeVisible?: boolean;
  setBladeVisible: () => void;
  onHandleClick: () => void;
}

const { GATSBY_ADA_LIVE_CHAT_ID } = process.env;

const ButtonLink = ({
  buttonText,
  buttonColor = 'red',
  buttonSize = 'medium',
  ariaLabel = '',
  url = '',
  route,
  spinner = false,
  className = '',
  children,
  bladeVisible = false,
  setBladeVisible,
  onHandleClick,
  ...props
}: Props) => {
  const { locale } = useIntl();
  const buttonClass = `${buttonColor} ${buttonSize} ${className}`.trim();
  const childComponent = spinner ? (
    <Spinner data-testid="button-spinner" />
  ) : (
    ReactHtmlParser(buttonText)
  );

  const isKioskMode = isKiosk();

  switch (route) {
    // internal Gatsby Link
    case 'internal':
      return (
        <InternalButton
          type="button"
          to={`/${locale}${url}`}
          aria-label={ariaLabel}
          className={buttonClass}
          onClick={() => {
            onHandleClick?.();
          }}
          {...props}
        >
          {children || childComponent}
        </InternalButton>
      );

    // external link
    case 'external':
      return isKioskMode ? null : (
        <ExternalButton
          type="button"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={ariaLabel}
          className={buttonClass}
          {...props}
        >
          {children || childComponent}
        </ExternalButton>
      );

    // chat widget button
    case 'chat-widget':
      return (
        <FunctionalButton
          type="button"
          aria-label={ariaLabel}
          className={`${buttonClass} functionalButton`}
          onClick={(e) => {
            openChatWidget(e, url);
            onHandleClick?.();
          }}
          {...props}
        >
          {children || childComponent}
        </FunctionalButton>
      );

    // live chat button
    case 'live-chat':
      return (
        <FunctionalButton
          type="button"
          aria-label={ariaLabel}
          className={`${buttonClass} functionalButton`}
          onClick={(e) => {
            openChatWidget(e, GATSBY_ADA_LIVE_CHAT_ID);
            onHandleClick?.();
          }}
          {...props}
        >
          {children || childComponent}
        </FunctionalButton>
      );

    // button that takes blade open function
    default:
      return (
        <FunctionalButton
          type="button"
          aria-label={ariaLabel}
          className={`${buttonClass} functionalButton`}
          onClick={() => {
            setBladeVisible?.(!bladeVisible);
            onHandleClick?.();
          }}
          {...props}
        >
          {children || childComponent}
        </FunctionalButton>
      );
  }
};

export default ButtonLink;
